import {
  HttpInterceptorFn,
  HttpRequest,
  HttpResponse,
  HttpEvent
} from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, filter, finalize, shareReplay, tap } from 'rxjs/operators';
import { ApiUrls } from '../../enum/routes';
import { CacheOptions, CLEAR_CACHE_CONTEXT } from './http-cache.model';

interface CacheEntry {
  src: string;
  data?: HttpResponse<unknown>;
  /** Set only after a successful HTTP response (never on failed/401). */
  ttl?: number;
}

const cacheMap = new Map<string, CacheEntry>();

/** In-flight network calls keyed by request fingerprint (dedupes concurrent callers). */
const inFlightRequests = new Map<string, Observable<HttpEvent<unknown>>>();

const cacheOptions: CacheOptions = {
  urlsToCache: [ApiUrls.homes, ApiUrls.home, ApiUrls.exchangeDetail],
  globalTTL: 600000 // 10 min
};

export function getHttpRequestDedupeKey(req: HttpRequest<unknown>): string {
  return getUniqueKey(req);
}

/** Drop cached entry for this request (e.g. before retrying after token refresh). */
export function invalidateHttpCacheForRequest(req: HttpRequest<unknown>): void {
  const key = getUniqueKey(req);
  cacheMap.delete(key);
  inFlightRequests.delete(key);
}

export const httpCacheInterceptor: HttpInterceptorFn = (req, next) => {
  const { urlsToCache = [] } = cacheOptions ?? {};
  const key = getUniqueKey(req);
  const needCache = urlsToCache.some((x) => new RegExp(x).test(req.url));
  const forceClearCache = req.context.get(CLEAR_CACHE_CONTEXT);

  if (needCache && forceClearCache) {
    cacheMap.delete(key);
    inFlightRequests.delete(key);
  }

  if (needCache && !forceClearCache) {
    const cached = cacheMap.get(key);
    if (cached?.data && cached.ttl && cached.ttl > Date.now()) {
      return of(cached.data.clone() as HttpResponse<unknown>);
    }

    const inFlight = inFlightRequests.get(key);
    if (inFlight) {
      return inFlight;
    }
  }

  const request$ = next(req).pipe(
    filter((event): event is HttpResponse<unknown> => event instanceof HttpResponse),
    tap((event) => {
      if (!needCache) {
        return;
      }
      cacheMap.set(key, {
        src: req.url,
        data: event,
        ttl: getTTL(req.url)
      });
    }),
    catchError((error) => {
      if (needCache) {
        cacheMap.delete(key);
        inFlightRequests.delete(key);
      }
      return throwError(() => error);
    }),
    finalize(() => {
      inFlightRequests.delete(key);
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  if (needCache && !forceClearCache) {
    inFlightRequests.set(key, request$);
  }

  return request$;
};

function getUniqueKey(req: HttpRequest<unknown>): string {
  const bodySorted = sortObjectByKeys(req.body);
  const auth = req.headers.get('Authorization') ?? '';
  return `${req.method}_${req.url}_${req.params.toString()}_${auth}_${JSON.stringify(bodySorted)}`;
}

function sortObjectByKeys(obj: unknown): unknown {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }
  const record = obj as Record<string, unknown>;
  const keysSorted = Object.keys(record).sort();
  return keysSorted.reduce<Record<string, unknown>>((_obj, key) => {
    const val = record[key];
    _obj[key] =
      typeof val === 'object' && val !== null ? sortObjectByKeys(val) : val;
    return _obj;
  }, {});
}

function getTTL(reqUrl: string): number {
  const { ttls, globalTTL } = cacheOptions ?? {};

  const matchedKey = Object.keys(ttls ?? {}).find((x) =>
    reqUrl.split('?')[0].endsWith(x)
  );

  return Date.now() + (matchedKey ? ttls![matchedKey] : globalTTL ?? 0);
}
